<script lang="ts" setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import type { HomePageConfig } from '../configs/types'
import ModuleBanner from './modules/ModuleBanner.vue'
import ModuleGrid2x4 from './modules/ModuleGrid2x4.vue'
import ModuleFeatured1Plus4 from './modules/ModuleFeatured1Plus4.vue'
import ModuleGrid1x4 from './modules/ModuleGrid1x4.vue'
import ModuleFeed from './modules/ModuleFeed.vue'
import ModuleTags2x3 from './modules/ModuleTags2x3.ficmoon.vue'
import ModuleRanking from './modules/ModuleRanking.vue'
import ModuleBookSwiper from './modules/ModuleBookSwiper.vue'
import { theme as brandTheme } from '@/config/brands'
import { bannerShow } from '@/composables/useBannerShow'
import { COLUMN_TYPE } from '@/constants'

const props = defineProps<{
  config: HomePageConfig
  setInfiniteRef: (el: HTMLElement | null) => void
  setInputRef: (el: any) => void
  input: string
  inputFocused: boolean
  pageList: any[]
  pageInfo: any
  skeleton: boolean
  modules: any[]
  currentTagId: string
  feeds: any[]
  feedShowName: string
  feedId: string
  feedsColumnType: string | number
  feedFetching: boolean
  feedEnding: boolean
  homeBgUrl: any
  switchNavTrack: (item: any) => void
  onSearch: () => void
  gotoCollection: (id: string) => void
  gotoTags: (id: string) => void
  gotoBook: (id: string, firstChapterId?: string) => void
  getCurrentTagResources: (col: any) => any[]
  getCurrentShowTags: (col: any) => any[]
  handleTagClick: (tagId: string) => void
}>()

// 定义 emit 以保持与 default 组件的类型兼容性
defineEmits<{
  (e: 'update:input', value: string): void
}>()

const localHomeBgUrl = ref(props.homeBgUrl)
watch(() => props.homeBgUrl, (newVal) => {
  localHomeBgUrl.value = newVal
}, { immediate: true })

// 定义样式变量供 CSS v-bind 使用
const homeBgUrlComputed = computed(() => `url(${localHomeBgUrl.value})`)
const progressGradient = computed(() => props.config.theme.tabActiveGradient)
const stickyHeaderBg = computed(() => props.config.theme.stickyHeaderBg || brandTheme.bgGlobal)
const tabTextColor = computed(() => brandTheme.textTertiary)
const tabActiveTextColor = computed(() => brandTheme.textPrimary)

// 顶部 Banner: 仅当 modules[0].columnType === BANNER 时生效
const bannerModule = computed(() => {
  const first = props.modules[0]
  return first?.columnType === COLUMN_TYPE.BANNER ? first : null
})
const restModules = computed(() => bannerModule.value ? props.modules.slice(1) : props.modules)
const hasBanner = computed(() => !!bannerModule.value)

// 滚动监听 + sticky-header 透明态切换
const scrollTop = ref(0)
const scrollContainer = ref<HTMLElement | null>(null)
const showHeaderBg = computed(() => {
  if (!hasBanner.value)
    return true
  return scrollTop.value > 60
})

function handleScroll(e: Event) {
  const target = e.target as HTMLElement
  scrollTop.value = target.scrollTop
}

function setScrollRef(el: HTMLElement | null) {
  if (scrollContainer.value)
    scrollContainer.value.removeEventListener('scroll', handleScroll)
  scrollContainer.value = el
  if (el)
    el.addEventListener('scroll', handleScroll, { passive: true })
  props.setInfiniteRef(el)
}

onUnmounted(() => {
  if (scrollContainer.value)
    scrollContainer.value.removeEventListener('scroll', handleScroll)
})
</script>

<template>
  <div :ref="setScrollRef" class="h-100dvh overflow-y-scroll">
    <!-- Sticky header: Tab + Search 浮在顶部 -->
    <div class="sticky-header" :class="{ 'show-bg': showHeaderBg, 'on-banner': hasBanner && !showHeaderBg }">
      <div class="flex items-center justify-between space-x-20px px-15px py-2.5">
        <!-- Tab -->
        <div class="overflow-x-auto flex-1">
          <div class="flex items-center space-x-30px">
            <div v-for="item in pageList" :key="item.id">
              <div class="home-tab relative" :class="{ active: item.id === pageInfo.id }" @click="switchNavTrack(item)">
                <img v-if="item.id === pageInfo.id && config.theme.showHotTabIcon !== false" src="/images/ficmoon/tags/hot-icon-red.png" class="absolute top-0 -right-3 w-7 h-7">
                <span class="text-14px whitespace-nowrap">{{ item.showName }}</span>
              </div>
            </div>
          </div>
        </div>
        <!-- Search -->
        <img v-if="pageList && pageList.length" src="/images/ficmoon/search-icon.png" width="20px" height="20px" class="search-icon-img cursor-pointer shrink-0" @click="onSearch">
      </div>
    </div>

    <!-- 顶部 Banner: 大背景，向上偏移延伸到 sticky-header 下方 -->
    <div v-if="bannerModule" class="banner-wrapper">
      <ModuleBanner
        :module="bannerModule"
        :config="config"
        position="top"
        :goto-book="gotoBook"
      />
    </div>

    <div class="home-bg pb-20" :class="{ 'with-banner': hasBanner }">
      <!-- Modules -->
      <div v-if="skeleton" class="h-100dvh">
        <div class="pt-20vh flex justify-center">
          <BasicSpinner class="mx-auto" />
        </div>
        <p class="mt-4 text-center font-bold">
          {{ config.texts.loading }}
        </p>
      </div>
      <div v-else>
        <div v-for="(col, index) in restModules" :key="col.id" :class="{ 'mt-24px': index > 0 }">
          <!-- 非顶部 Banner -->
          <template v-if="col.columnType === COLUMN_TYPE.BANNER">
            <div class="px-15px">
              <ModuleBanner
                :module="col"
                :config="config"
                position="inline"
                :goto-book="gotoBook"
              />
            </div>
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
          <!-- 轮播作品（SLIDE_BOOK 改用 BookSwiper 与 SWIPER 对齐） -->
          <template v-else-if="col.columnType === COLUMN_TYPE.SLIDE_BOOK">
            <ModuleBookSwiper :module="col" :config="config" card-class="p-0 px-15px" class="my-4 w-full" />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
          <!-- 新增2行3列的模块 -->
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_2_MUL_COL_3">
            <ModuleTags2x3
              card-class="p-0"
              :module="col"
              :config="config"
              :current-tag-id="currentTagId"
              :goto-book="gotoBook"
              :goto-tags="gotoTags"
              :get-current-tag-resources="getCurrentTagResources"
              :get-current-show-tags="getCurrentShowTags"
              :handle-tag-click="handleTagClick"
            />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_1_PLUS_COL_4">
            <ModuleFeatured1Plus4
              card-class="p-0 px-15px"
              :module="col"
              :config="config"
              :goto-book="gotoBook"
              :goto-collection="gotoCollection"
            />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_2_MUL_COL_4">
            <ModuleGrid2x4
              card-class="p-0 px-15px"
              :module="col"
              :config="config"
              :goto-book="gotoBook"
              :goto-collection="gotoCollection"
            />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_1_MUL_COL_4">
            <ModuleGrid1x4
              card-class="p-0 px-15px"
              :module="col"
              :config="config"
              :goto-book="gotoBook"
              :goto-collection="gotoCollection"
            />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_3_MUL_COL_4">
            <ModuleRanking :module="col" :config="config" card-class="p-0 px-15px" class="my-4 w-full" />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.SWIPER">
            <ModuleBookSwiper :module="col" :config="config" card-class="p-0 px-15px" class="my-4 w-full" />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
        </div>
      </div>

      <!-- Feed -->
      <ModuleFeed
        v-if="feeds.length"
        :key="pageInfo.id"
        card-class="p-0 px-15px mt-6"
        :feeds="feeds"
        :feed-show-name="feedShowName"
        :feed-id="feedId"
        :feeds-column-type="feedsColumnType"
        :feed-fetching="feedFetching"
        :feed-ending="feedEnding"
        :config="config"
        :goto-book="gotoBook"
      />
    </div>

    <BasicTabbar />
  </div>
</template>

<style lang="scss" scoped>
/* Sticky header */
.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: transparent;
  transition: background-color 0.3s ease;
}

.sticky-header.show-bg {
  background: v-bind(stickyHeaderBg);
}

/* Tab */
.home-tab {
  cursor: pointer;
}

.home-tab span {
  color: v-bind(tabTextColor);
  transition: color 0.3s ease;
}

.home-tab.active span {
  position: relative;
  line-height: 20px;
  font-size: 20px;
  color: v-bind(tabActiveTextColor);
  font-weight: bold;
  z-index: 1;
}

.home-tab.active i {
  position: absolute;
  left: 0;
  bottom: 0;
  margin-bottom: 2px;
  width: 100%;
  height: 6px;
  background-image: v-bind(progressGradient);
  border-radius: 3px;
  z-index: 0;
}

/* on-banner（在 banner 大背景上）：浅色文字 + 白色搜索图标 */
.sticky-header.on-banner .home-tab span,
.sticky-header.on-banner .home-tab.active span {
  color: #ffffff;
}

.search-icon-img {
  transition: filter 0.3s ease;
}

.sticky-header.on-banner .search-icon-img {
  filter: brightness(0) invert(1);
}

/* Banner-wrapper: 向上偏移，让 banner 延伸到 sticky-header 下方 */
.banner-wrapper {
  position: relative;
  margin-top: -55px;
  z-index: 0;
}

/* home-bg：保留 ficmoon 原有的伪元素模糊背景，但有顶部 banner 时禁用 */
.home-bg {
  position: relative;
  z-index: 1;
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 300px;
    z-index: -1;
    background-image: v-bind(homeBgUrlComputed);
    background-repeat: no-repeat;
    background-position: top left;
    background-size: 100% auto;
    -webkit-mask-image: linear-gradient(rgba(0, 0, 0, .3), transparent);
    mask-image: linear-gradient(rgba(0, 0, 0, .3), transparent);
    filter: blur(5px);
    transition: background 3s;
  }
}

.home-bg.with-banner::before {
  display: none;
}

/* 有顶部 Banner 时：home-bg 略微上移，避免与 banner 底部之间出现 sub-pixel 缝隙 */
.home-bg.with-banner {
  margin-top: -1px;
  position: relative;
  z-index: 1;
}
</style>
